import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Package, TrendingUp, Globe, ArrowRight, X as XIcon, CheckCircle2, Loader2 } from 'lucide-react';
import { sendDemoEmail, DemoFormData } from '../lib/sendDemoEmail';
const roles = ['Mill Owner / Manufacturer', 'Trader / Broker', 'Exporter / Importer', 'Retailer / Brand', 'Investor', 'Other'];
function DemoModal({
  onClose
}: {
  onClose: () => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<DemoFormData>({
    name: '',
    email: '',
    phone: '',
    role: '',
    company: ''
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await sendDemoEmail(form);
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-477">
      <motion.div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-478" />
      <motion.div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" initial={{
      opacity: 0,
      scale: 0.94,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.94,
      y: 20
    }} transition={{
      ease: [0.34, 1.56, 0.64, 1],
      duration: 0.4
    }} data-id="element-479">
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F5F4F0] flex items-center justify-center hover:bg-[#E8E4DC] transition-colors z-10" data-id="element-480">
          <XIcon className="w-4 h-4 text-[#5C5A55]" data-id="element-481" />
        </button>
        {!submitted ? <div className="p-8" data-id="element-482">
            <div className="mb-6" data-id="element-483">
              <h2 className="font-jakarta text-2xl font-extrabold text-[#0F0E0C] mb-1" data-id="element-484">
                Request a Demo
              </h2>
              <p className="text-sm text-[#9B9890] font-inter" data-id="element-485">
                Our team will get in touch with you shortly.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-486">
              {[{
            label: 'Full Name',
            key: 'name',
            type: 'text',
            placeholder: 'Rahul Sharma',
            required: true
          }, {
            label: 'Email',
            key: 'email',
            type: 'email',
            placeholder: 'rahul@company.com',
            required: true
          }, {
            label: 'Phone Number',
            key: 'phone',
            type: 'tel',
            placeholder: '+91 98765 43210',
            required: true
          }].map(field => <div key={field.key} data-id="element-487">
                  <label className="block text-xs font-inter font-semibold text-[#5C5A55] mb-1.5 uppercase tracking-wide" data-id="element-488">
                    {field.label} *
                  </label>
                  <input type={field.type} required value={(form as any)[field.key]} onChange={e => setForm({
              ...form,
              [field.key]: e.target.value
            })} placeholder={field.placeholder} className="w-full px-4 py-2.5 rounded-xl border border-[#E8E4DC] bg-[#FAFAF8] text-[#0F0E0C] text-sm font-inter focus:outline-none focus:border-[#E8500A] focus:ring-2 focus:ring-[#E8500A]/10 transition-all" data-id="element-489" />
                </div>)}
              <div data-id="element-490">
                <label className="block text-xs font-inter font-semibold text-[#5C5A55] mb-1.5 uppercase tracking-wide" data-id="element-491">
                  Role *
                </label>
                <select required value={form.role} onChange={e => setForm({
              ...form,
              role: e.target.value
            })} className="w-full px-4 py-2.5 rounded-xl border border-[#E8E4DC] bg-[#FAFAF8] text-[#0F0E0C] text-sm font-inter focus:outline-none focus:border-[#E8500A] focus:ring-2 focus:ring-[#E8500A]/10 transition-all appearance-none" data-id="element-492">
                  <option value="" data-id="element-493">Select your role</option>
                  {roles.map(r => <option key={r} value={r} data-id="element-494">
                      {r}
                    </option>)}
                </select>
              </div>
              <div data-id="element-495">
                <label className="block text-xs font-inter font-semibold text-[#5C5A55] mb-1.5 uppercase tracking-wide" data-id="element-496">
                  Company
                </label>
                <input type="text" value={form.company} onChange={e => setForm({
              ...form,
              company: e.target.value
            })} placeholder="Your company name" className="w-full px-4 py-2.5 rounded-xl border border-[#E8E4DC] bg-[#FAFAF8] text-[#0F0E0C] text-sm font-inter focus:outline-none focus:border-[#E8500A] focus:ring-2 focus:ring-[#E8500A]/10 transition-all" data-id="element-497" />
              </div>

              {error && <p className="text-xs text-red-500 font-inter bg-red-50 px-3 py-2 rounded-lg" data-id="element-498">
                  {error}
                </p>}

              <motion.button type="submit" disabled={loading} whileHover={!loading ? {
            scale: 1.02
          } : {}} whileTap={!loading ? {
            scale: 0.98
          } : {}} className="w-full py-3 bg-[#E8500A] hover:bg-[#D14508] disabled:opacity-60 disabled:cursor-not-allowed text-white font-inter font-semibold rounded-xl transition-colors mt-2 flex items-center justify-center gap-2" data-id="element-499">
                {loading ? <>
                    <Loader2 className="w-4 h-4 animate-spin" data-id="element-500" />
                    Sending...
                  </> : 'Request Demo'}
              </motion.button>
            </form>
          </div> : <div className="p-8 text-center" data-id="element-501">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-502">
              <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-503" />
            </motion.div>
            <h3 className="font-jakarta text-xl font-extrabold text-[#0F0E0C] mb-2" data-id="element-504">
              Request Received!
            </h3>
            <p className="text-sm text-[#5C5A55] font-inter leading-relaxed mb-6" data-id="element-505">
              Our team will get in touch with you shortly.
            </p>
            <motion.button onClick={onClose} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="px-6 py-2.5 bg-[#0F0E0C] text-white font-inter font-semibold rounded-xl text-sm" data-id="element-506">
              Close
            </motion.button>
          </div>}
      </motion.div>
    </motion.div>;
}
const features = [{
  icon: Package,
  title: 'Verified Listings',
  description: 'Browse thousands of verified yarn listings from trusted mills and traders across India.'
}, {
  icon: TrendingUp,
  title: 'Live Price Discovery',
  description: 'Get real-time market prices and trends to make informed buying and selling decisions.'
}, {
  icon: Globe,
  title: 'Pan-India Network',
  description: 'Connect with buyers and sellers from Mumbai, Surat, Ludhiana, Tiruppur, and beyond.'
}];
export function Marketplace() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [modalOpen, setModalOpen] = useState(false);
  return <>
      <section id="marketplace" ref={ref} className="py-24 bg-white" data-id="element-507">
        <div className="max-w-6xl mx-auto px-5 lg:px-8" data-id="element-508">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-509">
            <p className="text-xs font-inter font-semibold text-[#E8500A] tracking-[0.18em] uppercase mb-4" data-id="element-510">
              The Marketplace
            </p>
            <h2 className="font-jakarta text-4xl md:text-5xl font-extrabold text-[#0F0E0C] mb-4 leading-tight" data-id="element-511">
              Buy & Sell Yarn,
              <br data-id="element-512" />
              Simplified
            </h2>
            <p className="text-[#5C5A55] font-inter max-w-xl mx-auto text-base leading-relaxed" data-id="element-513">
              The Yarn Bazaar brings the entire yarn supply chain online — from
              spinning mills to fabric manufacturers.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-514">
            {features.map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 24,
            filter: 'blur(4px)'
          }} animate={isInView ? {
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          } : {}} transition={{
            duration: 0.6,
            delay: i * 0.1
          }} className="bg-[#FAFAF8] rounded-2xl p-7 border border-[#E8E4DC] hover:border-[#D4CFC5] hover:shadow-md hover:shadow-black/5 transition-all duration-300 card-lift" data-id="element-515">
                <div className="w-10 h-10 rounded-xl bg-[#E8500A]/10 flex items-center justify-center mb-5" data-id="element-516">
                  <feature.icon className="w-5 h-5 text-[#E8500A]" data-id="element-517" />
                </div>
                <h3 className="font-jakarta font-extrabold text-[#0F0E0C] text-lg mb-2" data-id="element-518">
                  {feature.title}
                </h3>
                <p className="text-sm text-[#5C5A55] font-inter leading-relaxed" data-id="element-519">
                  {feature.description}
                </p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.35
        }} className="text-center" data-id="element-520">
            <motion.button onClick={() => setModalOpen(true)} whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} className="magnetic-btn inline-flex items-center gap-2 px-7 py-3.5 bg-[#E8500A] hover:bg-[#D14508] text-white font-jakarta font-bold rounded-full text-sm transition-colors shadow-md shadow-[#E8500A]/20" data-id="element-521">
              Request Demo
              <ArrowRight className="w-4 h-4" data-id="element-522" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      <AnimatePresence data-id="element-523">
        {modalOpen && <DemoModal onClose={() => setModalOpen(false)} data-id="element-524" />}
      </AnimatePresence>
    </>;
}